import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import { CheckCircleIcon } from '@icons';
import { useEventContext } from '@providers/EventProvider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import { useClientSpecificGuidedTutorialsVisibility } from '@hooks/clientSpecificUtils';

import { useCompletedTutorials, useTutorial } from '../TutorialProvider';
import { TutorialId } from '../interfaces';
import { TUTORIAL_ORDER, TUTORIAL_REGISTRY } from '../registry';
import { useTutorialPromptGate } from './hooks/useTutorialPromptGate';
import { useStyles } from './styles';

type Stage = 'prompt' | 'choices';

/**
 * Prompts the user, once per browser, to run a guided tutorial right after
 * landing in the experience. Continuing shows a radio choice of the
 * available tutorials, marking any already completed with a check icon;
 * picking one or skipping persists the decision so the prompt never shows
 * again. A forced open (the home-bar test button) skips the initial ask and
 * opens straight on the tutorial choices, since that ask is only meaningful
 * on the natural landing-in-experience trigger — its dismiss button reads
 * "Cancel" instead of "Skip" in that case, since there's nothing being
 * skipped. Renders nothing at all while the experience's `GuidedTutorials`
 * feature flag is off.
 */
const TutorialPromptModal = () => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { isEventRun } = useEventContext();
  const { currentScreen } = useMytaverse();
  const { startTutorial, isPromptForced, closeTutorialPrompt } = useTutorial();
  const { hasDecided, recordDecision } = useTutorialPromptGate();
  const completedTutorialIds = useCompletedTutorials();
  const { isGuidedTutorialsEnabled } =
    useClientSpecificGuidedTutorialsVisibility();

  const [stage, setStage] = useState<Stage>('prompt');
  const [selectedId, setSelectedId] = useState<TutorialId | null>(null);

  // Gated on the experience feature flag, not just the HomeBar test button —
  // otherwise the natural "landed in the experience" trigger below would
  // still show the prompt for experiences that haven't opted in.
  const isOpen =
    isGuidedTutorialsEnabled &&
    (isPromptForced ||
      (!hasDecided && isEventRun && currentScreen === CurrentScreenEnum.WORLD));

  // Forced opens (the home-bar test button) skip straight to the tutorial
  // choices — the "Would you like to complete tutorial?" ask only makes
  // sense the first time, right after landing in the experience.
  useEffect(() => {
    if (isPromptForced) {
      setStage('choices');
    }
  }, [isPromptForced]);

  const handleSkip = () => {
    recordDecision('skipped');
    closeTutorialPrompt();
  };

  const handleContinue = () => {
    setStage('choices');
  };

  const handleStart = () => {
    if (!selectedId) {
      return;
    }

    recordDecision(selectedId);
    startTutorial(selectedId);
    closeTutorialPrompt();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleSkip}
      data-testid="tutorial-prompt-dialog"
      PaperProps={{ sx: classes.dialogPaper }}
      TransitionProps={{
        // Reset stage/selection only once the closing fade finishes —
        // resetting on isOpen flipping false (via an effect) changed the
        // rendered content mid-fade, visible as the prompt-stage title
        // flashing while the dialog was still closing.
        onExited: () => {
          setStage('prompt');
          setSelectedId(null);
        },
      }}
    >
      {stage === 'prompt' ? (
        <>
          <DialogTitle>{translate('guidedTutorial.promptTitle')}</DialogTitle>
          <DialogActions>
            <Button onClick={handleSkip} data-testid="tutorial-prompt-skip-btn">
              {translate(
                isPromptForced
                  ? 'guidedTutorial.promptCancel'
                  : 'guidedTutorial.promptSkip',
              )}
            </Button>
            <Button
              onClick={handleContinue}
              data-testid="tutorial-prompt-continue-btn"
            >
              {translate('guidedTutorial.promptContinue')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>{translate('guidedTutorial.chooseTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <RadioGroup
                value={selectedId ?? ''}
                onChange={(event) =>
                  setSelectedId(event.target.value as TutorialId)
                }
              >
                {TUTORIAL_ORDER.map((id) => {
                  const isCompleted = completedTutorialIds.includes(id);

                  return (
                    <FormControlLabel
                      key={id}
                      value={id}
                      control={<Radio />}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          {translate(TUTORIAL_REGISTRY[id].titleKey)}
                          {isCompleted && (
                            <Box
                              data-testid={`tutorial-choice-${id}-completed`}
                            >
                              <CheckCircleIcon />
                            </Box>
                          )}
                        </Box>
                      }
                      data-testid={`tutorial-choice-${id}`}
                    />
                  );
                })}
              </RadioGroup>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSkip} data-testid="tutorial-prompt-skip-btn">
              {translate(
                isPromptForced
                  ? 'guidedTutorial.promptCancel'
                  : 'guidedTutorial.promptSkip',
              )}
            </Button>
            <Button
              onClick={handleStart}
              disabled={!selectedId}
              data-testid="tutorial-prompt-start-btn"
            >
              {translate('guidedTutorial.chooseStart')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TutorialPromptModal;
